import type {
  T_VisitorMessage,
  T_VisitorMessageAuthorRaw,
  T_VisitorMessageFile,
  T_VisitorMessageFileRaw,
  T_VisitorMessageRaw,
  T_VisitorMessageTopic,
  T_VisitorMessageTopicEntry,
  T_VisitorMessageTopicEntryRaw,
  T_VisitorMessageTopicRaw,
  T_VisitorUnreadMessage,
} from './types'

const toFiniteNumber = (value: unknown, fallback = 0) => {
  const num = Number(value)
  return Number.isFinite(num) ? num : fallback
}

const toSafeString = (value: unknown) => (
  typeof value === 'string' ? value : ''
)

const toDate = (value: unknown) => {
  const asNumber = toFiniteNumber(value, 0)
  const timestamp = asNumber > 0 && asNumber < 1e12
    ? asNumber * 1000
    : asNumber

  if (timestamp > 0) {
    return new Date(timestamp)
  }

  const asString = toSafeString(value).trim()
  const parsed = asString ? Date.parse(asString) : Number.NaN
  if (Number.isFinite(parsed)) {
    return new Date(parsed)
  }

  return new Date()
}

export const mapVisitorMessageFile = (
  raw: T_VisitorMessageFileRaw | null | undefined
): T_VisitorMessageFile => {
  return {
    id: toFiniteNumber(raw?.id, 0),
    title: toSafeString(raw?.title),
    path: toSafeString(raw?.path),
  }
}

export const mapVisitorMessageAuthor = (raw: T_VisitorMessageAuthorRaw | null | undefined) => {
  const login = toSafeString(raw?.login)
  const fullname = toSafeString(raw?.fullname)

  return {
    id: toFiniteNumber(raw?.id, 0),
    login,
    fullname,
    photoPath: toSafeString(raw?.photo?.path),
  }
}

export const mapVisitorMessageTopic = (
  raw: T_VisitorMessageTopicRaw | null | undefined
): T_VisitorMessageTopic => {
  return {
    id: toFiniteNumber(raw?.id ?? raw?.topic_id, 0),
    isSystem: !!raw?.is_system,
    subject: toSafeString(raw?.subject ?? raw?.topic_subject ?? raw?.title),
    author: mapVisitorMessageAuthor(raw?.author),
  }
}

export const mapVisitorMessageTopicEntry = (
  raw: T_VisitorMessageTopicEntryRaw | null | undefined
): T_VisitorMessageTopicEntry => {
  const topic = mapVisitorMessageTopic(raw?.topic)

  return {
    ...topic,
    lastMessageTime: toDate(raw?.last_message_time),
  }
}

export const mapVisitorMessage = (
  raw: T_VisitorMessageRaw | null | undefined
): T_VisitorMessage => {
  return {
    id: toFiniteNumber(raw?.id, 0),
    timestamp: toDate(raw?.time ?? raw?.timestamp ?? raw?.created_at),
    content: toSafeString(raw?.content ?? raw?.text ?? raw?.message),
    files: Array.isArray(raw?.files) ? raw!.files.map(mapVisitorMessageFile) : [],
    isRead: !!(raw?.is_read ?? raw?.read),
    isDashboardShow: !!(raw?.is_dashboard_show ?? raw?.isDashboardShow),
    author: mapVisitorMessageAuthor(raw?.author),
  }
}

export const mapVisitorUnreadMessage = (
  messageRaw: T_VisitorMessageRaw | null | undefined,
  topicRaw: T_VisitorMessageTopicRaw | null | undefined
): T_VisitorUnreadMessage => {
  const mappedMessage = mapVisitorMessage(messageRaw)
  const mappedTopic = mapVisitorMessageTopic(topicRaw)
  const topicIdFromMessage = toFiniteNumber(messageRaw?.topic_id, 0)
  const topicSubjectFromMessage = toSafeString(messageRaw?.subject)

  return {
    ...mappedMessage,
    topic: {
      ...mappedTopic,
      id: mappedTopic.id > 0 ? mappedTopic.id : topicIdFromMessage,
      subject: mappedTopic.subject || topicSubjectFromMessage,
      author: mappedTopic.author.id > 0
        ? mappedTopic.author
        : mappedMessage.author,
    },
  }
}
